<template>
  <footer>
    <modules-language-drop-down />
    <div class="area_info">
      <div class="box_link">
        <a
          v-for="(linkInfo, idx) in linkList"
          :key="idx"
          target="_blank"
          class="link_item"
          :class="[linkInfo.class, { before_separator: idx > 0 }]"
          @click.prevent="goToLink(linkInfo.link)"
        >
          {{ linkInfo.text }}
        </a>
      </div>
      <div class="box_address">
        <div
          v-for="(addrLine, idx) in addrList"
          :key="idx"
          class="line_address"
        >
          <span
            v-for="(addr, childIdx) in addrLine"
            :key="childIdx"
            v-dompurify-html="addr"
            :class="{ before_separator: childIdx > 0 }"
          ></span>
        </div>
      </div>
      <span class="txt_copyright">
        ⓒ NEXON Korea Corporation All Rights Reserved.
      </span>
      <atoms-c-image file-name="logo-nexon" :density="3" />
    </div>
  </footer>
</template>

<script lang="ts" setup>
import { TERMS_KEY, HELLOMAPLE_BIZ_EMAIL } from '@/utils/constants'
const authStore = useAuthStore()
const { isKR } = storeToRefs(authStore)
const termsStore = useTermsStore()
const localePath = useLocalePath()
const { t } = useI18n()

const linkList = computed(() => [
  {
    text: t('footer_1'),
    link: {
      termsKey: TERMS_KEY.TERMS_OF_USE
    }
  },
  {
    text: t('footer_2'),
    link: {
      termsKey: TERMS_KEY.PRIVACY_POLICY
    },
    class: 'bold'
  },
  ...(isKR.value
    ? [
        {
          text: t('footer_3'),
          link: {
            termsKey: TERMS_KEY.IP_USAGE_GUIDE
          }
        },
        {
          text: t('footer_4'),
          link: {
            termsKey: TERMS_KEY.POLICY_FOR_YOUNGER
          }
        },
        {
          text: t('footer_5'),
          link: {
            termsKey: TERMS_KEY.PLATFORM_USAGE_GUIDE
          }
        }
      ]
    : [])
])

const addrList = computed(() => [
  [
    ...t('footer_6')
      .split('|')
      .map((el) => el.trim()), // 영문 푸터는 | 구분자가 텍스트에 포함됨
    t('footer_7'),
    t('footer_8')
  ],
  [
    t('footer_9', { mail: HELLOMAPLE_BIZ_EMAIL }),
    t('footer_10'),
    t('footer_11')
  ]
])

async function goToLink(link: { termsKey: TermsKey<CountryCodeOfGeoIP> }) {
  const { termsKey } = link
  const { id, type } = await termsStore.getTermsInfo(termsKey)
  window.open(
    localePath({ path: `/legal/${type.toLowerCase()}`, query: { id } }),
    '_blank'
  )
}
</script>
<!-- 언어별 스타일 -->
<style lang="scss">
html[lang='ko-KR'] {
  footer {
    letter-spacing: -0.56px;

    .area_info {
      .box_link {
        letter-spacing: -0.6px;
      }
    }
  }
}

@media (max-width: 999px) {
  html[lang='ko-KR'] {
    footer {
      .area_info {
        .box_link {
          letter-spacing: -0.56px;
        }
      }
    }
  }
}
</style>
<style lang="scss" scoped>
footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #120937;
  color: $white;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;

  .area_info {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 0;
    gap: 8px;

    .box_link {
      font-size: 15px;
      display: flex;
      gap: 16px;
      .link_item {
        cursor: pointer;
      }
      .bold {
        font-weight: 700;
      }
    }
    .box_address {
      opacity: 0.5;
      display: flex;
      flex-direction: column;
      gap: 4px;
      .line_address {
        display: flex;
        flex-wrap: wrap;
        column-gap: 16px;
        row-gap: 0;
        justify-content: center;
      }
    }
    .before_separator {
      position: relative;
      &:before {
        content: '';
        position: absolute;
        height: 8px;
        width: 1px;
        left: -8px;
        top: calc(50% - 4px);
        background-color: #ffffff;
      }
    }
    .txt_copyright {
      font-weight: 700;
      opacity: 0.3;
      margin-bottom: 16px; // +gap 8px;
    }
  }
}

@media (max-width: 999px) {
  footer {
    .area_info {
      gap: 12px;
      padding: 16px 20px;
      .box_link {
        flex-wrap: wrap;
        gap: 4px 16px;
        font-size: 14px;
        justify-content: center;
      }
      .box_address {
        max-width: 480px;
        text-align: center;
        gap: 6px;
        .line_address {
          display: flex;
          justify-content: center;
          gap: 2px;
          &:nth-child(1) {
            flex-wrap: wrap;
          }
          &:nth-child(2) {
            flex-direction: column;
          }
          .before_separator {
            &:before {
              display: none;
            }
          }
        }
      }
      .txt_copyright {
        letter-spacing: -0.48px; // 언어 무관 적용 중(en으로만 노출)
        font-size: 12px;
        margin-bottom: 12px; // +gap 12px;
      }
    }
  }
}
</style>
